import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import { useGetAccountBalancesQuery } from "../APIs/MirrorNode.api";
import { useEffect, useState } from "react";
import {
  DEFAULT_NOTIFICATION_DELAY,
  NETWORK_TOKEN_PRICE_URL
} from "../utils/constants";
import {
  useWatchTransactionReceipt,
  useWriteContract
} from "@buidlerlabs/hashgraph-react-wallets";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons";

const ContractBalance = ({ contractId }) => {
  const { writeContract } = useWriteContract();
  const { watch } = useWatchTransactionReceipt();
  const {
    data: contractBalanceData,
    isLoading: isLoadingContractBalance,
    refetch: refetchContractBalance
  } = useGetAccountBalancesQuery(contractId);
  const [hbarPriceUSD, setHbarPriceUSD] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const balanceHBAR =
    !isLoadingContractBalance && contractBalanceData
      ? contractBalanceData.balance / Math.pow(10, 8)
      : 0;

  const handleWithdraw = async () => {
    return new Promise(async (resolve, reject) => {
      setIsLoading(true);

      showNotification({
        id: "withdraw-contract-funds",
        loading: true,
        title: "Withdraw",
        message: `Withdrawing ${balanceHBAR}ℏ from ${contractId}`,
        autoClose: false,
        disallowClose: true
      });

      try {
        const transactionIdOrHash = await writeContract({
          contractId,
          functionName: "withdraw",
          abi: [
            {
              inputs: [],
              name: "withdraw",
              outputs: [],
              stateMutability: "nonpayable",
              type: "function"
            }
          ]
        });

        watch(transactionIdOrHash, {
          onSuccess(transaction) {
            updateNotification({
              id: "withdraw-contract-funds",
              color: "teal",
              title: "Withdraw",
              message: "Funds withdrawn successfully",
              icon: <IconCheck size={16} />,
              autoClose: DEFAULT_NOTIFICATION_DELAY
            });

            setIsLoading(false);
            refetchContractBalance();
            resolve(transaction);
            return transaction;
          },
          onError(transaction, error) {
            const errorMessage =
              error.errorName === "Error"
                ? error.args.join(",")
                : error.errorName;

            updateNotification({
              id: "withdraw-contract-funds",
              color: "red",
              title: "Withdraw failed",
              message: errorMessage,
              icon: <IconX size={16} />,
              autoClose: 4000
            });

            setIsLoading(false);
            reject(error);
            return transaction;
          }
        });
      } catch (e) {
        console.error(e);

        const errorMessage = JSON.stringify(e.message).includes("USER_REJECT")
          ? "User rejected action"
          : e.message;

        updateNotification({
          id: "withdraw-contract-funds",
          color: "red",
          title: "Withdraw failed",
          message: errorMessage,
          icon: <IconX size={16} />,
          autoClose: 4000
        });

        setIsLoading(false);
        reject(errorMessage);
      }
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const response = await fetch(NETWORK_TOKEN_PRICE_URL);
        const data = await response.json();

        setHbarPriceUSD(data.market_data.current_price.usd);
      } catch (error) {
        console.error("Couldn't load HBAR price:", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Paper p="sm" mb="lg" withBorder>
      <Stack spacing="sm">
        <Group align="flex-end" position="apart">
          <Stack spacing={0}>
            <Text style={{ lineHeight: 1 }} weight="bold" size="1.6rem">
              {balanceHBAR}ℏ
            </Text>
            <Text size="xs" color="dimmed">
              {balanceHBAR * hbarPriceUSD}$
            </Text>
          </Stack>
          <Button
            onClick={handleWithdraw}
            disabled={balanceHBAR === 0}
            loading={isLoadingContractBalance || isLoading}
          >
            withdraw
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};

export default ContractBalance;
